import React, { SyntheticEvent, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAppDispatch } from 'app/hooks';
import { FormMsg, NoFormMsg } from 'components/forms/FormMessage';
import Icon from 'components/icons/Icon';
import { IconSymbol, IconVariant } from 'components/icons/Icon.types';
import { ModalDialog } from 'components/modal/Modal';
import { useModal } from 'components/modal/ModalContext';
import UserAvatar from 'components/user/UserAvatar';
import { IUser, Role } from 'features/user/userAPI';
import { emptyUser } from 'features/user/userSlice';
import { deleteUserAsync } from 'features/users/usersSlice';
import { sameId } from 'helpers/objects';
import { getPattern } from 'helpers/regex';
import { useUser } from 'selectors/userSelectors';

import './UserDataForm.scss';

export type UserSettings = {
  username: string;
  email: string;
  admin: boolean;
  manager: boolean;
  isVerified: boolean;
  password?: string;
  registrationCode?: string;
};

export type UserDataFormProps = {
  editedUser?: IUser;
  onSubmit: (userData: UserSettings) => boolean;
};

function UserDataForm(props: UserDataFormProps) {
  const { onSubmit, editedUser } = props;
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const { user } = useUser();
  const { openModal, closeModal } = useModal();

  const focusRef = useRef<HTMLInputElement | null>(null);
  const [formMsg, setFormMsg] = useState<FormMsg>(NoFormMsg);

  const isNew = !editedUser;
  const u = editedUser ?? emptyUser;
  const [userData, setUserData] = useState<UserSettings>({
    username: u.username ?? '',
    email: u.email ?? '',
    admin: u.roles?.includes(Role.admin) ?? false,
    manager: u.roles?.includes(Role.manager) ?? false,
    isVerified: u.isVerified ?? false,
    password: isNew ? '' : undefined,
  });

  function handleSubmit(): boolean {
    const canClose = onSubmit(userData);
    return canClose;
  }

  function handleDeleteUser() {
    if (!editedUser) return;
    function doDelete(): boolean {
      dispatch(deleteUserAsync({ id: editedUser!.id! }));
      closeModal();
      return true;
    }
    openModal(
      <ModalDialog
        className="pd_alert"
        title={<FormattedMessage id="USERS.FORM.DELETE_USER" />}
        okBtn={{
          label: <FormattedMessage id="X.DELETE" />,
          onClick: doDelete,
        }}
        cancelBtn={{
          isDefault: true,
        }}
      >
        <div className="title">
          <FormattedMessage id="USERS.FORM.DELETE_USER_CONF" />
          <br />
          <span className="user_deleting">
            {userData.username} - {userData.email}
          </span>
        </div>
        <div className="text">
          <FormattedMessage id="USERS.FORM.DELETE_USER_CONF_2" />
        </div>
        <Icon
          symbol={IconSymbol.alert}
          variant={IconVariant.accent}
          size={80}
        />
        <div className="text">
          <FormattedMessage id="GENERAL.DELETE.NOTICE" />
        </div>
      </ModalDialog>,
    );
  }

  function handleCheckboxChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, checked } = event.target;
    setUserData((prev) => {
      return {
        ...prev,
        [name]: checked,
      };
    });
  }

  function handleInputFieldChange(e: SyntheticEvent) {
    const target = e.target as HTMLInputElement;
    setUserData((prev) => ({
      ...prev,
      [target.name]: target.value,
    }));
  }

  return (
    <ModalDialog
      className="user_status_form narrow"
      title={
        isNew ? (
          <FormattedMessage id="USERS.FORM.ADD_USER" />
        ) : (
          <FormattedMessage id="USERS.FORM.USER_SETTINGS" />
        )
      }
      okBtn={{
        onClick: handleSubmit,
      }}
      cancelBtn={{}}
      enableEscape
      formMsg={formMsg}
    >
      {isNew ? null : (
        <div className="user_id">
          <UserAvatar user={u} small />
          ID: {u.id}
        </div>
      )}
      <div className="user_name">
        <Form.Control
          className="field"
          type="text"
          name="username"
          placeholder="name"
          autoComplete="off"
          value={userData.username || ''}
          onChange={handleInputFieldChange}
          required
          // pattern={getPattern('username')}
          ref={focusRef}
        />
      </div>
      <div className="user_email">
        <Form.Control
          className="field"
          type="email"
          name="email"
          placeholder="email"
          autoComplete="off"
          value={userData.email || ''}
          onChange={handleInputFieldChange}
          pattern={getPattern('email')}
          // required
        />
      </div>
      {isNew ? null : (
        <>
          <div>
            <Form.Check
              id={'isVerified'}
              type="checkbox"
              label={intl.formatMessage({ id: 'USERS.FORM.VERIFIED' })}
              name="isVerified"
              checked={userData.isVerified}
              onChange={handleCheckboxChange}
              disabled={sameId(u, user)}
            />
            <Form.Check
              id="admin"
              type="checkbox"
              label="Admin"
              name="admin"
              checked={userData.admin}
              onChange={handleCheckboxChange}
              disabled={sameId(u, user)}
            />
            <Form.Check
              id="manager"
              type="checkbox"
              label="Manager"
              name="manager"
              checked={userData.manager}
              onChange={handleCheckboxChange}
              disabled={sameId(u, user)}
            />
          </div>
          <div className="delete_user_section">
            <div onClick={handleDeleteUser}>
              <Icon symbol={IconSymbol.bin} />
              <FormattedMessage id="USERS.FORM.DELETE_USER" />
            </div>
          </div>
        </>
      )}
      {isNew ? (
        <>
          <div className="user_password">
            <Form.Control
              type="password"
              name="password"
              autoComplete="new-password"
              placeholder="password"
              value={userData.password || ''}
              onChange={handleInputFieldChange}
              required
              pattern={getPattern('password')}
            />
          </div>
        </>
      ) : null}
    </ModalDialog>
  );
}

const MemoizedUserDataForm = React.memo(UserDataForm);
export default MemoizedUserDataForm;
